import { useEffect, useState } from 'react';

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isAndroid = () => /Android/.test(navigator.userAgent);
const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
const isMobile = () => isIOS() || isAndroid() || /Mobile|webOS/.test(navigator.userAgent);

export const usePWAInstall = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    // Only dismiss for 24 hours, not forever
    const ts = localStorage.getItem('pwa-dismissed-at');
    if (ts && Date.now() - parseInt(ts) < 86400000) return true;
    return false;
  });

  useEffect(() => {
    if (isStandalone()) { setIsInstalled(true); return; }
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    const installed = () => setIsInstalled(true);
    window.addEventListener('appinstalled', installed);
    return () => { window.removeEventListener('beforeinstallprompt', handler); window.removeEventListener('appinstalled', installed); };
  }, []);

  const install = async () => {
    if (!installPrompt) return false;
    installPrompt.prompt();
    const r = await installPrompt.userChoice;
    setInstallPrompt(null);
    return r.outcome === 'accepted';
  };

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-dismissed-at', String(Date.now()));
  };

  const canNativeInstall = !!installPrompt && !isInstalled;
  const showIOSGuide = isIOS() && !isInstalled && !dismissed;
  const showAndroidGuide = isAndroid() && !installPrompt && !isInstalled && !dismissed;
  const canInstall = (canNativeInstall || showIOSGuide || showAndroidGuide) && !dismissed;

  return {
    canInstall,
    canNativeInstall,
    isInstalled,
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    isMobile: isMobile(),
    install,
    dismiss,
    showIOSGuide,
    showAndroidGuide,
  };
};
